// src/pages/SkillsPage.tsx
//
// Reforma UX §6.7 (wireframe seção 08 "Skills — Ativos", SKL-01..05).
// Registry de skills em CARDS (sem avatar) + filtros (busca · scope · status)
// + "Nova skill" (CreateAssetModal escopo livre). Detalhe vira MODAL sobre a
// lista (SkillDetailModal); /skills/:id é deep-link que abre o modal (wrapper
// em SkillDetailPage.tsx — mesma técnica recomendada no §10.2 do doc).
//
// Filtros reais: scope é server-side (GET /v1/skills?scope=); status
// (published='ativa' · draft · deprecated='arquivada') e busca são client-side
// — o backend aceita ?status= mas api.listSkills (intocável nesta wave) não o
// propaga; a lista retorna `status` no payload, então filtramos aqui.

import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, type Skill, type SkillScope } from '../lib/api';
import { Card } from '@/components/ui/Card';
import { Skeleton } from '@/components/ui/Skeleton';
import { EmptyState } from '@/components/ui/EmptyState';
import { PageHeader } from '@/components/layout/PageHeader';
import { Sparkles, Plus, Search, Globe, Copy, Trash2, ChevronLeft, ChevronRight } from 'lucide-react';
import '@/styles/wf-agents.css'; // chrome compartilhado: .dfilters/.search/.dnewbtn
import '@/styles/wf-skills.css';
import { Button } from '@/components/ui/Button';
import { CreateAssetModal } from '@/components/CreateAssetModal';
import { SkillExecuteModal } from '@/components/SkillExecuteModal';
import { ConfirmActionDialog } from '@/components/ConfirmActionDialog';
import { buildPromoteFeedback, getPromoteCopy } from '@/lib/promote-feedback';
import { SkillCard } from '@/components/skills/SkillCard';
import { SkillDetailModal } from '@/components/skills/SkillDetailModal';
import { SCOPE_FILTER_OPTIONS, STATUS_FILTER_OPTIONS } from '@/components/skills/skill-constants';

export interface SkillsPageProps {
  /** Deep-link /skills/:id — abre o modal de detalhe sobre a lista. */
  detailId?: string;
}

// SKL-A (admin 2026-06-12): quantidade por página configurável — mesmo padrão
// da tela de Agentes (AGT-11). Default 10.
const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];
const DEFAULT_PAGE_SIZE = 10;

export function SkillsPage({ detailId }: SkillsPageProps) {
  const navigate = useNavigate();
  // Anti-bug Radix: ao fechar o menu de contexto (Portal), o "click fantasma"
  // pós-pointerup cai no card clicável por baixo → abriria o detalhe. Suprime
  // a navegação do card por ~300ms quando o menu fecha.
  const suppressCardNavRef = useRef(false);

  const [skills, setSkills] = useState<Skill[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [search, setSearch] = useState('');
  const [scopeFilter, setScopeFilter] = useState<SkillScope | ''>('');
  const [statusFilter, setStatusFilter] = useState('');
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [currentPage, setCurrentPage] = useState(1);

  const [createModeOpen, setCreateModeOpen] = useState(false);
  const [executeSkill, setExecuteSkill] = useState<Skill | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const [promoteTarget, setPromoteTarget] = useState<Skill | null>(null);
  const [instantiateTarget, setInstantiateTarget] = useState<Skill | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Skill | null>(null);

  const flashToast = (msg: string, ms = 6000) => {
    setToast(msg);
    setTimeout(() => setToast(null), ms);
  };

  const openDetail = (skill: Skill) => {
    if (suppressCardNavRef.current) return;
    navigate(`/skills/${skill.id}`);
  };

  const closeDetail = () => navigate('/skills');

  const confirmDelete = async (slug: string) => {
    // DELETE /v1/skills/:slug — soft delete (deleted_at), audit via trigger 126.
    await api.deleteSkill(slug);
    setSkills((curr) => curr.filter((s) => s.slug !== slug));
    flashToast(`Skill ${slug} excluída (soft-delete).`);
  };

  const confirmPromote = async (skill: Skill) => {
    // POST /v1/skills/:slug/promote — cascata (agentes da skill) + sync storage.
    const result = await api.promoteSkill(skill.slug, { target_scope: 'global', force: true });
    flashToast(buildPromoteFeedback('skill', skill.name || skill.slug, result));
  };

  const confirmInstantiate = async (slug: string) => {
    // POST /v1/skills/:slug/install — clona a global no tenant atual.
    const result = await api.installSkill(slug);
    flashToast(`Skill ${slug} instanciada no tenant (skill_id: ${result.skill_id.slice(0, 8)}…).`);
  };

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const data = await api.listSkills({ scope: scopeFilter || undefined });
        if (!cancelled) setSkills(data);
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Erro ao carregar skills');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [scopeFilter]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return skills
      .filter((s) => {
        if (statusFilter) {
          const status = (s as unknown as { status?: string }).status;
          if (status !== statusFilter) return false;
        }
        if (!q) return true;
        return (
          s.slug.toLowerCase().includes(q) ||
          (s.name ?? '').toLowerCase().includes(q) ||
          (s.description ?? '').toLowerCase().includes(q)
        );
      })
      // SKL-D (admin 2026-06-12): ordem ALFABÉTICA — mais fácil de achar
      // (mesma decisão da tela de Agentes).
      .sort((a, b) =>
        (a.name || a.slug).localeCompare(b.name || b.slug, 'pt-BR', { sensitivity: 'base' }),
      );
  }, [skills, search, statusFilter]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const effectivePage = Math.min(Math.max(1, currentPage), totalPages);
  const paginated = useMemo(() => {
    const start = (effectivePage - 1) * pageSize;
    return filtered.slice(start, start + pageSize);
  }, [filtered, effectivePage, pageSize]);

  const onMenuOpenChange = (open: boolean) => {
    if (!open) {
      suppressCardNavRef.current = true;
      window.setTimeout(() => {
        suppressCardNavRef.current = false;
      }, 300);
    }
  };

  return (
    <div>
      <PageHeader
        title="Skills"
        description="Registry de skills — SKILL.md + references + evals + telemetria"
        className="mb-2"
      />

      {/* Filtros: busca · scope · status + Nova skill (wireframe §08 .dfilters) */}
      <div className="dfilters">
        <div className="search" style={{ flex: 1, minWidth: 170 }}>
          <Search className="ic" aria-hidden />
          <input
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setCurrentPage(1);
            }}
            placeholder="Buscar skill"
            aria-label="Buscar skill"
          />
        </div>
        <select
          value={scopeFilter}
          onChange={(e) => {
            setScopeFilter(e.target.value as SkillScope | '');
            setCurrentPage(1);
          }}
          aria-label="Filtro de scope"
        >
          {SCOPE_FILTER_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
        <select
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value);
            setCurrentPage(1);
          }}
          aria-label="Filtro de status"
        >
          {STATUS_FILTER_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
        {/* SKL-A: quantidade por página (mesmo padrão da tela de Agentes) */}
        <select
          value={String(pageSize)}
          onChange={(e) => {
            setPageSize(Number(e.target.value));
            setCurrentPage(1);
          }}
          aria-label="Skills por página"
        >
          {PAGE_SIZE_OPTIONS.map((n) => (
            <option key={n} value={n}>{n} por página</option>
          ))}
        </select>
        <button type="button" className="dnewbtn" onClick={() => setCreateModeOpen(true)}>
          <Plus className="ic" aria-hidden />
          Nova skill
        </button>
      </div>

      {error && (
        <Card className="bg-raios-danger/10 px-4 py-3 mb-4">
          <p className="text-raios-danger-text text-sm">{error}</p>
        </Card>
      )}

      {loading ? (
        <div className="skl-grid">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <Skeleton key={i} className="h-44 rounded-xl" />
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <EmptyState
          icon={Sparkles}
          title="Nenhuma skill encontrada"
          description="Crie sua primeira skill ou ajuste os filtros."
          action={{ label: 'Nova skill', onClick: () => setCreateModeOpen(true) }}
        />
      ) : (
        <>
        <div className="skl-grid">
          {paginated.map((s) => (
            <SkillCard
              key={s.id}
              skill={s}
              onClick={openDetail}
              onMenuOpenChange={onMenuOpenChange}
              onViewDetails={() => navigate(`/skills/${s.id}`)}
              onDispatch={() => setExecuteSkill(s)}
              onPromote={() => setPromoteTarget(s)}
              onInstantiate={() => setInstantiateTarget(s)}
              onDelete={() => setDeleteTarget(s)}
            />
          ))}
        </div>
        {filtered.length > pageSize && (
          <div className="flex items-center justify-between px-1 py-4">
            <p className="text-xs text-raios-muted">
              Página {effectivePage} de {totalPages} · {filtered.length}{' '}
              {filtered.length === 1 ? 'skill' : 'skills'}
            </p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={effectivePage === 1}
                onClick={() => setCurrentPage(Math.max(1, effectivePage - 1))}
                aria-label="Página anterior"
              >
                <ChevronLeft className="w-4 h-4" />
                Anterior
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={effectivePage >= totalPages}
                onClick={() => setCurrentPage(Math.min(totalPages, effectivePage + 1))}
                aria-label="Próxima página"
              >
                Próxima
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}
        </>
      )}

      {/* Detalhe em MODAL sobre a lista (SKL-02) — deep-link /skills/:id */}
      {detailId && (
        <SkillDetailModal
          skillId={detailId}
          open
          onClose={closeDetail}
          onDeleted={(slug) => {
            setSkills((curr) => curr.filter((s) => s.slug !== slug));
            flashToast(`Skill ${slug} excluída (soft-delete).`);
            closeDetail();
          }}
        />
      )}

      {/* Nova skill — modal de escopo livre (F8, reuso) */}
      <CreateAssetModal
        type="skill"
        open={createModeOpen}
        onClose={() => setCreateModeOpen(false)}
        onSubmitted={(msg) => flashToast(msg, 7000)}
      />

      {/* Disparar skill da lista (menu ⋮) */}
      {executeSkill && (
        <SkillExecuteModal
          open={!!executeSkill}
          onClose={() => setExecuteSkill(null)}
          skill={{
            slug: executeSkill.slug,
            name: executeSkill.name,
            version: executeSkill.version,
            inputs_schema:
              (executeSkill as unknown as { inputs_schema?: Record<string, unknown> }).inputs_schema ?? {},
          }}
          onDispatched={(taskId) => {
            flashToast(
              `Skill ${executeSkill.slug} disparada — task ${taskId.slice(0, 8)}. Acompanhe em Execuções no detalhe.`,
            );
          }}
        />
      )}

      {/* Toast inline */}
      {toast && (
        <div className="fixed bottom-4 right-4 z-[600] max-w-md rounded-lg border border-raios-border bg-raios-surface px-4 py-3 shadow-lg text-sm">
          {toast}
        </div>
      )}

      {/* Promover (tenant→global) */}
      {promoteTarget && (
        <ConfirmActionDialog
          open={!!promoteTarget}
          onOpenChange={(o) => {
            if (!o) setPromoteTarget(null);
          }}
          title={getPromoteCopy('skill').title}
          description={getPromoteCopy('skill').description}
          confirmLabel={getPromoteCopy('skill').confirmLabel}
          confirmVariant="brand"
          confirmIcon={Globe}
          onConfirm={() => confirmPromote(promoteTarget)}
        >
          <p className="text-sm text-raios-text-secondary">
            Skill: <span className="font-mono text-raios-text">{promoteTarget.slug}</span>
            {promoteTarget.name && ` — ${promoteTarget.name}`}
          </p>
        </ConfirmActionDialog>
      )}

      {/* Instanciar (global→tenant) */}
      {instantiateTarget && (
        <ConfirmActionDialog
          open={!!instantiateTarget}
          onOpenChange={(o) => {
            if (!o) setInstantiateTarget(null);
          }}
          title="Instanciar skill no seu tenant?"
          description="Cria uma cópia desta skill global no seu tenant pra customização. A original continua disponível."
          confirmLabel="Instanciar"
          confirmVariant="brand"
          confirmIcon={Copy}
          onConfirm={() => confirmInstantiate(instantiateTarget.slug)}
        >
          <p className="text-sm text-raios-text-secondary">
            Skill: <span className="font-mono text-raios-text">{instantiateTarget.slug}</span>
            {instantiateTarget.name && ` — ${instantiateTarget.name}`}
          </p>
        </ConfirmActionDialog>
      )}

      {/* Excluir (soft-delete + confirmação) */}
      {deleteTarget && (
        <ConfirmActionDialog
          open={!!deleteTarget}
          onOpenChange={(o) => {
            if (!o) setDeleteTarget(null);
          }}
          title="Excluir skill?"
          description="Soft-delete no backend (recuperável por admin) — a skill some da listagem. Audit log captura via trigger."
          confirmLabel="Excluir"
          confirmVariant="destructive"
          confirmIcon={Trash2}
          onConfirm={() => confirmDelete(deleteTarget.slug)}
        >
          <p className="text-sm text-raios-text-secondary">
            Skill: <span className="font-mono text-raios-text">{deleteTarget.slug}</span>
            {deleteTarget.name && ` — ${deleteTarget.name}`}
          </p>
        </ConfirmActionDialog>
      )}
    </div>
  );
}
